// Master Bahan — Raw Material Master (Phase 1D-δ-1)
// List + create/edit dialog. Role gating per Owner brief.
//
// Pola daftar seragam (28 Sep 2026; `docs/POLA_DAFTAR.md`): mode SERVER penuh, keadaan di alamat halaman,
// saringan status bawaan "Aktif" (chip bisa dihapus → semua, termasuk nonaktif — dulu bahan nonaktif
// TIDAK PERNAH tampil karena backend menyaring aktif secara bawaan) + kategori.
import { Link } from "react-router-dom";
import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Package, Plus, RefreshCw, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  listMaterials, createMaterial, updateMaterial, getMaterial, apiErrorMessage,
} from "../../lib/inventoryApi";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

// Enam nilai = CHECK DB `raw_material_master_category_check` = enum backend
// `MaterialCategory`. Dulu daftar ini menawarkan LAINNYA (DB tolak) dan
// menyembunyikan TINTA/BENANG/PACKAGING/OTHER — kelas cacat yang sama dengan UOM.
const CATEGORIES = ["KAIN", "TINTA", "BENANG", "AKSESORI", "PACKAGING", "OTHER"];
// Tujuh nilai = CHECK DB `raw_material_master_uom_check` = enum backend
// `routers/inventory.py::MaterialUoM`. Dulu daftar ini memuat LUSIN yang DB
// tolak dan menyembunyikan LITER/PACK/OTHER (MATERIALS-JSX-UOM-LUSIN-DITOLAK-DB-01).
const UOM = ["METER", "KG", "LITER", "PCS", "ROLL", "PACK", "OTHER"];
// Kode → kata yang tampil (nilai yang dikirim tetap kode).
const LABEL_KATEGORI = { KAIN: "Kain", TINTA: "Tinta", BENANG: "Benang", AKSESORI: "Aksesori", PACKAGING: "Kemasan", OTHER: "Lainnya" };
const LABEL_UOM = { METER: "Meter", KG: "Kg", LITER: "Liter", PCS: "Pcs", ROLL: "Roll", PACK: "Pack", OTHER: "Lainnya" };
const satuan = (u) => LABEL_UOM[u] || u || "satuan";
const LABEL_AKTIF = { AKTIF: "Aktif", NONAKTIF: "Nonaktif" };
//: Cermin `URUT_SAH_BAHAN` di backend/services/inventory_service.py.
const URUT_SAH = ["code", "name", "category", "unit_of_measure", "min_stock_level", "created_at"];
const KOLOM_CSV = [
  { kunci: "code", judul: "Kode" }, { kunci: "name", judul: "Nama" },
  { kunci: "category", judul: "Kategori", ambil: (m) => LABEL_KATEGORI[m.category] || m.category || "" },
  { kunci: "unit_of_measure", judul: "Satuan", ambil: (m) => satuan(m.unit_of_measure) },
  { kunci: "min_stock_level", judul: "Min. Stok" },
  { kunci: "is_active", judul: "Status", ambil: (m) => (m.is_active ? "Aktif" : "Nonaktif") },
];

export default function Materials() {
  const { roleCode } = useAuth();
  // Redesign 2026-07-29: master bahan hanya diisi/diedit Owner + Kepala
  // Cabang. Admin Gudang turun jadi read-only.
  const canWrite = roleCode === "OWNER" || roleCode === "KEPALA_CABANG";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "code", arah: "asc" },
    saringan: {
      aktif: { label: "Status", param: "status_aktif", bawaan: "AKTIF", tampil: (v) => LABEL_AKTIF[v] || v },
      kategori: { label: "Kategori", param: "category", tampil: (v) => LABEL_KATEGORI[v] || v },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  // ⚠ Saringan status DIHAPUS = "SEMUA" dikirim eksplisit: tanpa parameter, backend tetap
  //   menyaring bahan aktif saja (bawaan lamanya) dan chip yang hilang akan berbohong.
  const bangunParams = useCallback((o = {}) => ({
    status_aktif: "SEMUA", ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listMaterials(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create; obj = edit
  const [form, setForm] = useState({
    code: "", name: "", category: "KAIN", unit_of_measure: "METER",
    min_stock_level: 0, is_active: true,
  });
  const [saving, setSaving] = useState(false);

  // SALDO-PER-GUDANG-NOL-PUNYA-LAYAR-01: saldo per gudang (GET /inventory/materials/{id} → stock_by_store,
  // dijepit wilayah di service — keputusan Owner 6 Sep 2026). Dibuka per baris, bukan dimuat untuk semua bahan.
  const [saldoBuka, setSaldoBuka] = useState(null);
  const [saldo, setSaldo] = useState({});
  const bukaSaldo = async (m) => {
    if (saldoBuka === m.id) { setSaldoBuka(null); return; }
    setSaldoBuka(m.id);
    if (saldo[m.id]) return;
    try {
      const r = await getMaterial(m.id);
      setSaldo((x) => ({ ...x, [m.id]: r.data.stock_by_store || [] }));
    } catch (e) { toast.error(apiErrorMessage(e, "Saldo per gudang gagal dimuat.")); setSaldoBuka(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // ⛔ `q`, BUKAN `search` (P-4 sisiran param, 6 Sep 2026): `search` DIABAIKAN endpoint ini.
      const r = await listMaterials(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat master bahan."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      code: "", name: "", category: "KAIN", unit_of_measure: "METER",
      min_stock_level: 0, is_active: true,
    });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      code: row.code, name: row.name, category: row.category,
      unit_of_measure: row.unit_of_measure,
      min_stock_level: row.min_stock_level ?? 0,
      is_active: row.is_active,
      over_receive_tolerance_pct: row.over_receive_tolerance_pct ?? "",
      potong_atasan_per_satuan: row.potong_atasan_per_satuan ?? "",
      potong_celana_per_satuan: row.potong_celana_per_satuan ?? "",
      tambahan_per_ukuran: Object.entries(row.tambahan_per_ukuran || {}).map(([u, p]) => `${u}=${p}`).join(", "),
    });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Kode & nama wajib diisi.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        // Kolom kosong = dikosongkan (null dikirim eksplisit); angka dikirim sebagai angka.
        const badan = { ...form };
        ["over_receive_tolerance_pct", "potong_atasan_per_satuan", "potong_celana_per_satuan"].forEach((k) => {
          badan[k] = badan[k] === "" || badan[k] == null ? null : Number(badan[k]);
        });
        // Vonis Owner 25 Sep sore: konversi kain 0 = tidak dipakai untuk bagian itu = kosong (NULL).
        ["potong_atasan_per_satuan", "potong_celana_per_satuan"].forEach((k) => { if (badan[k] === 0) badan[k] = null; });
        const perUkuran = {};
        for (const bagian of String(form.tambahan_per_ukuran || "").split(",")) {
          if (!bagian.trim()) continue;
          const [u, p] = bagian.split("=");
          if (!u || !u.trim() || p === undefined || Number.isNaN(Number(p))) {
            toast.error(`Tambahan per ukuran tidak terbaca: "${bagian.trim()}" — tulis seperti XXL=10, 3XL=15`);
            setSaving(false);
            return;
          }
          perUkuran[u.trim().toUpperCase()] = Number(p);
        }
        badan.tambahan_per_ukuran = perUkuran;
        // K197 butir 7: angka konversi kain diisi OWNER — peran lain tidak mengirimnya sama sekali.
        if (roleCode !== "OWNER") {
          delete badan.potong_atasan_per_satuan;
          delete badan.potong_celana_per_satuan;
          delete badan.tambahan_per_ukuran;
        }
        await updateMaterial(editing.id, badan);
        toast.success("Bahan berhasil diperbarui.");
      } else {
        await createMaterial(form);
        toast.success("Bahan baru berhasil dibuat.");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan bahan."));
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="space-y-6" data-testid="materials-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Package className="h-6 w-6" /> Master Bahan Baku
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Kelola daftar bahan baku (kain, aksesori, dll) untuk WO & pengadaan.{" "}
            <Link to="/inventory/alarm-bahan" className="underline" data-testid="materials-ke-alarm">Alarm bahan</Link>
            {["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG"].includes(roleCode) && (
              <>{" · "}<Link to="/inventory/pindah-bahan" className="underline" data-testid="materials-ke-pindah-bahan">Pindah bahan antar gudang</Link></>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="materials-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
          </Button>
          {canWrite && (
            <Button size="sm" onClick={openCreate} data-testid="materials-create-btn">
              <Plus className="h-4 w-4 mr-1" /> Bahan Baru
            </Button>
          )}
        </div>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari kode / nama bahan..." testId="materials-search"
                    ekspor={{ nama: "master_bahan", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={daftar.nilaiSaringan.aktif} onChange={(e) => daftar.setSaringan("aktif", e.target.value)}
                className="h-9 rounded border px-2 text-sm" aria-label="Saring status" data-testid="materials-saring-aktif">
          <option value="">Semua status</option>
          <option value="AKTIF">Aktif</option>
          <option value="NONAKTIF">Nonaktif</option>
        </select>
        <select value={daftar.nilaiSaringan.kategori} onChange={(e) => daftar.setSaringan("kategori", e.target.value)}
                className="h-9 rounded border px-2 text-sm" aria-label="Saring kategori" data-testid="materials-saring-kategori">
          <option value="">Semua kategori</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{LABEL_KATEGORI[c]}</option>)}
        </select>
      </PalangDaftar>

      <Card className="overflow-hidden" data-testid="materials-table">
        {loading && daftar.tampil.length === 0 ? (
          <div className="p-8 space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : daftar.tampil.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm" data-testid="materials-kosong">
            {daftar.cari.trim() || daftar.saringanAktif.length
              ? "Tidak ada bahan yang cocok dengan pencarian atau saringan ini."
              : <>Belum ada bahan baku terdaftar.{canWrite && " Klik ‘Bahan Baru’ untuk menambahkan."}</>}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="code" className="px-4 py-3 font-medium">Kode</ThUrut>
                <ThUrut daftar={daftar} kunci="name" className="px-4 py-3 font-medium">Nama</ThUrut>
                <ThUrut daftar={daftar} kunci="category" className="px-4 py-3 font-medium">Kategori</ThUrut>
                <ThUrut daftar={daftar} kunci="unit_of_measure" className="px-4 py-3 font-medium">Satuan</ThUrut>
                <ThUrut daftar={daftar} kunci="min_stock_level" className="px-4 py-3 font-medium">Min. Stok</ThUrut>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Saldo</th>
                {canWrite && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {daftar.tampil.map((m) => (
                <React.Fragment key={m.id}>
                <tr className="border-b hover:bg-muted/50">
                  <td className="px-4 py-3 font-mono text-xs">{m.code}</td>
                  <td className="px-4 py-3">{m.name}</td>
                  <td className="px-4 py-3">{LABEL_KATEGORI[m.category] || m.category || "—"}</td>
                  <td className="px-4 py-3">{satuan(m.unit_of_measure)}</td>
                  <td className="px-4 py-3">{m.min_stock_level ?? "-"}</td>
                  <td className="px-4 py-3">
                    <Badge variant={m.is_active ? "default" : "outline"}
                           className={m.is_active ? "bg-success-soft text-success-soft-foreground" : ""}>
                      {m.is_active ? "Aktif" : "Nonaktif"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <button type="button" className="text-xs underline" onClick={() => bukaSaldo(m)}
                            data-testid={`materials-saldo-${m.id}`}>
                      {saldoBuka === m.id ? "Tutup" : "Per gudang"}
                    </button>
                  </td>
                  {canWrite && (
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(m)}
                              data-testid={`materials-edit-${m.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </td>
                  )}
                </tr>
                {saldoBuka === m.id && (
                  <tr className="border-b bg-muted/30">
                    <td colSpan={canWrite ? 8 : 7} className="px-4 py-2 text-sm" data-testid={`materials-saldo-isi-${m.id}`}>
                      {!saldo[m.id] ? "Memuat…" : saldo[m.id].length === 0 ? "Belum ada saldo di gudang wilayah Anda." : (
                        <span className="flex flex-wrap gap-x-6 gap-y-1">
                          {saldo[m.id].map((g) => (
                            <span key={g.store_id}>{g.store_name}: <b>{g.balance}</b> {satuan(m.unit_of_measure)}</span>
                          ))}
                        </span>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
        <Paginasi daftar={daftar} testId="materials-paginasi" />
      </Card>


      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="material-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Ubah Bahan" : "Bahan Baru"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm">Kode <span className="text-danger">*</span></span>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                     data-testid="material-code" placeholder="Mis. KAIN-DRIFIT-001"
                     disabled={!!editing} />
            </label>
            <label className="block">
              <span className="text-sm">Nama <span className="text-danger">*</span></span>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="material-name" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-sm">Kategori</span>
                <select value={form.category} data-testid="material-category"
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className="w-full h-9 rounded border px-2 text-sm">
                  {CATEGORIES.map(c => <option key={c} value={c}>{LABEL_KATEGORI[c]}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-sm">Satuan</span>
                <select value={form.unit_of_measure} data-testid="material-uom"
                        onChange={(e) => setForm({ ...form, unit_of_measure: e.target.value })}
                        className="w-full h-9 rounded border px-2 text-sm">
                  {UOM.map(u => <option key={u} value={u}>{LABEL_UOM[u]}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <span className="text-sm">Stok Minimum</span>
              <Input type="number" value={form.min_stock_level}
                     onChange={(e) => setForm({ ...form, min_stock_level: Number(e.target.value) })}
                     data-testid="material-min-stock" />
            </label>
            {editing && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="material-kolom-alarm">
                {/* ISIAN §13: toleransi terima-lebih per bahan (maks 10 %, kosong = 10 %). */}
                <label className="block"><span className="text-xs">Toleransi terima-lebih (%) — maks 10</span>
                  <Input type="number" min={0} max={10} value={form.over_receive_tolerance_pct}
                         onChange={(e) => setForm({ ...form, over_receive_tolerance_pct: e.target.value })} data-testid="material-toleransi" /></label>
                {/* Alarm bahan (K197 butir 7): lembar atasan / celana per satuan stok — diisi OWNER. Kosong = kain
                    ini tidak dipakai untuk bagian itu; FA yang tetap memakainya "belum bisa dihitung". */}
                <label className="block"><span className="text-xs">Potong atasan per {satuan(form.unit_of_measure)}</span>
                  <Input type="number" min={0} value={form.potong_atasan_per_satuan} disabled={roleCode !== "OWNER"}
                         onChange={(e) => setForm({ ...form, potong_atasan_per_satuan: e.target.value })} data-testid="material-potong-atasan" /></label>
                <label className="block"><span className="text-xs">Potong celana per {satuan(form.unit_of_measure)}</span>
                  <Input type="number" min={0} value={form.potong_celana_per_satuan} disabled={roleCode !== "OWNER"}
                         onChange={(e) => setForm({ ...form, potong_celana_per_satuan: e.target.value })} data-testid="material-potong-celana" /></label>
                <label className="block sm:col-span-3"><span className="text-xs">Tambahan kain per ukuran (%) — mis. XXL=10, 3XL=15 · kosong = belum ada</span>
                  <Input value={form.tambahan_per_ukuran} disabled={roleCode !== "OWNER"}
                         onChange={(e) => setForm({ ...form, tambahan_per_ukuran: e.target.value })} data-testid="material-tambahan-ukuran" /></label>
              </div>
            )}
            {editing && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_active}
                       onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                <span className="text-sm">Aktif</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="material-save-btn">
              {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
